// The Rust engine's boundary (docs/rust-engine/PLAN.md, contracts 3 and 4). All offsets are UTF-16 code units
// into the Markdown text; geometry is in editor content coordinates (CSS px from the top-left of the editor
// root's border box, scroll included).

export type Edit = { from: number; to: number; insert: string };

export interface PatchLines {
  start: number;
  deleteCount: number;
  insert: LineViewJs[];
}

export interface Patch {
  lines?: PatchLines | null;
  sel: { anchor: number; head: number };
  edits?: Edit[] | null;
  scroll?: "caret" | "start" | "end" | null;
  composing?: boolean | null;
}

export interface CutResult {
  text: string;
  patch: Patch;
}

/** editor-core SegView. Enum values may come in any case ("Marker", "marker"); see lineview.ts. */
export interface SegViewJs {
  kind: string;
  from: number;
  to: number;
  styles: string[];
  hidden: boolean;
  token?: string | null;
  cell?: number | null;
  el?: number | null;
}

export interface ImageViewJs {
  from: number;
  to: number;
  altRange: [number, number];
  alt: string;
  src: string;
  title?: string | null;
  revealed: boolean;
}

export interface LineViewJs {
  /** Stable identity for DOM reuse. */
  key: string;
  /** Length of the line's text (without the newline). All other offsets are relative to the line start. */
  len: number;
  textFrom: number;
  kind: string;
  level: number;
  checked: boolean;
  number: number;
  indent: number;
  gutterLevels: number;
  active: boolean;
  revealed: boolean;
  collapsed: boolean;
  code?: { first: boolean; last: boolean } | null;
  table?: { align: (string | null)[]; cells: number; columns: number } | null;
  segments: SegViewJs[];
  images: ImageViewJs[];
  imageOnly: boolean;
  /** A line with images split at them (absent: no images); see LinePartJs. */
  parts?: LinePartJs[] | null;
  /** The line's raw text. */
  text: string;
  /** A heading with a section to fold (absent: false). */
  foldable?: boolean | null;
  /** A folded heading: the lines of its section after it are hidden. */
  folded?: boolean | null;
  /** Hidden inside a folded section: takes no space, holds no caret position. */
  hiddenByFold?: boolean | null;
}

/**
 * A piece of a line with images (editor-core LinePart), top to bottom: a text part wraps in the column, an image block
 * (`images[first..end]`) sits on its own row(s) right after the text part holding its sources. A text part that isn't
 * `shown` has nothing visible (collapsed sources, whitespace) and takes no row. Offsets line-relative.
 */
export type LinePartJs = { kind: "text"; from: number; to: number; shown: boolean } | { kind: "images"; first: number; end: number };

export interface JsLayout {
  caret_rect(pos: number): [x: number, y: number, w: number, h: number];
  hit_test(x: number, y: number): number | undefined;
  bounds(): [w: number, h: number];
  /** The adjacent visual line's middle y; null: there is none (document edge); undefined: unknown (the engine probes). */
  adjacent_line_y(pos: number, dir: -1 | 1): number | null | undefined;
  table_cell_at(line: number, x: number, y: number): number | undefined;
  line_mid_y(line: number): number | undefined;
  image_at(x: number, y: number): [from: number, to: number] | undefined;
}

/** mods bitmask */
export const SHIFT = 1;
export const ALT = 2;
export const CTRL = 4;
export const META = 8;

export interface Engine {
  text(): string;
  render_all(): Patch;
  insert_text(s: string): Patch;
  key(key: string, mods: number, layout: JsLayout): Patch | undefined;
  mouse_down(x: number, y: number, clicks: number, shift: boolean, layout: JsLayout): Patch;
  mouse_drag(x: number, y: number, layout: JsLayout): Patch;
  set_selection(anchor: number, head: number): Patch;
  begin_composition(): void;
  /** Ends a composition: after a commit (`insert_text`) nothing changes; cancelled, the selection it replaced goes. */
  end_composition(): Patch;
  /** Forgets a composition without changing the text (the view showing it was rebuilt or went away). */
  abort_composition(): void;
  paste(text: string): Patch;
  cut(): CutResult;
  copy(): string;
  /** One edit replacing [from, to) (spelling corrections), without typing rules. */
  replace_range(from: number, to: number, text: string): Patch;
  undo(): Patch;
  redo(): Patch;
  toggle_todo(line: number): Patch;
  /** A link's pencil: expand the link at `pos` (its brackets and URL editable text, the caret at the end of its text). Optional: the fake engine has no links. */
  expand_link?(pos: number): Patch;
  set_read_only(ro: boolean): void;
  apply_remote(edits: Edit[]): Patch;
  /** Fold / unfold heading line `line` (view state: no text edit). Optional: the fake engine has no folding. */
  toggle_fold?(line: number): Patch;
  /** "toggle" | "fold" | "unfold" | "foldAll" | "unfoldAll" (the key map's fold commands). */
  fold_command?(which: string): Patch;
  /** The folded headings as [heading line text, occurrence] pairs (persisted per note). */
  fold_keys?(): [string, number][];
  set_fold_keys?(keys: [string, number][]): Patch;
  free?(): void;
}

export type EngineFactory = (text: string) => Engine;

// Collab engines (`Engine.collab(clientId, user)`, contract 4 "collab"): the engine owns the shared document.

export interface SaveStateJs {
  dirty: boolean;
  saving: boolean;
  commit: string | null;
  savedAt: number | null;
}

export type Notice =
  | { t: "hello"; readOnly: boolean; saveState: SaveStateJs }
  | { t: "synced" }
  | { t: "readOnly"; readOnly: boolean }
  | { t: "saveState"; saveState: SaveStateJs }
  | { t: "external"; commit: string | null }
  | { t: "reset" };

export interface Received {
  /** Remote edits (`edits` = the change). None when `reloaded` (render everything with `render_all`). */
  patch?: Patch | null;
  frames: Uint8Array[];
  notices: Notice[];
  awareness: boolean;
  reloaded: boolean;
  error?: string | null;
}

export interface Outgoing {
  frames: Uint8Array[];
  wakeInMs?: number | null;
  awareness: boolean;
}

export type AfterClose = { retry: number } | { closed: string; to: string | null };

export interface PeerCursor {
  client: number;
  userId: string;
  name: string;
  color: string;
  anchor: number;
  head: number;
  /** The cursor as published: changes only when that participant moves it. */
  key: string;
}

export interface CollabEngine extends Engine {
  /** `nowMs`: the same clock as take_outgoing's (performance.now()). */
  receive(frame: Uint8Array, nowMs: number): Received;
  /** Frames from take_outgoing / receive / set_focused couldn't be sent (socket closing): their edits are unsent. */
  frames_lost(): void;
  take_outgoing(nowMs: number): Outgoing;
  set_focused(focused: boolean, nowMs: number): Uint8Array[];
  connecting(): void;
  disconnected(code: number, reason: string): AfterClose;
  online(): boolean;
  reset(clientId: number): Patch;
  request_flush(): Uint8Array[];
  leave(): Uint8Array[];
  status(): string;
  synced(): boolean;
  read_only(): boolean;
  save_state(): SaveStateJs | null;
  client_id(): number;
  remote_cursors(): PeerCursor[];
  peers(): { client: number; state: unknown }[];
  is_consistent(): boolean;
  /** Awareness renewal / remote-state timeout in ms (v2 `welcome`, `renew`); non-positive keeps the current one. */
  set_awareness_timing?(renewMs: number, timeoutMs: number): void;
}
